import React, { FunctionComponent, MouseEvent } from 'react'
import { Button, Icon, IconSizes, PaymentIcon, Span, Text, Theme, useIsTheme, useThemeClass } from '@afterpaytouch/core'
import styles from './style.module.css'
import clsx from 'clsx'
import { useCheckoutSizes } from '../../utils/responsive'
import { useCheckoutColor } from '../../utils/color'
import { CashAppPayStatus, PaymentType } from '@afterpay/types'
import { useSelectedPaymentType } from '../../state'
import { useTranslation } from '../../utils/convergence'
import { useCachedFeatureFlag } from '../../state'
import { Features } from '../../model'

interface Props {
  cashtag: string
  status: CashAppPayStatus
  iconSize?: IconSizes
  reversed?: boolean
  showLearnMore?: boolean
  handleToggleTooltip?: () => void
  setShowCashAppPaymentMethodTooltip?: (show: boolean) => void
  tooltipRef?: React.RefObject<HTMLButtonElement>
  cashAppPaymentMethodTooltipRef?: React.RefObject<HTMLDivElement>
  testNameSpace?: string
  validatePaymentType?: boolean
  showCashAppPaymentMethodTooltip?: boolean
  isGALayout?: boolean
  hideInfoIcon?: boolean
}

export const PaymentMethodCashAppPay: FunctionComponent<Props> = ({
  iconSize,
  cashtag,
  status,
  reversed = false,
  showLearnMore = false,
  handleToggleTooltip = () => {},
  testNameSpace = '',
  tooltipRef = null,
  validatePaymentType = false,
  showCashAppPaymentMethodTooltip = false,
  setShowCashAppPaymentMethodTooltip = () => {},
  cashAppPaymentMethodTooltipRef = null,
  isGALayout = false,
  hideInfoIcon = false,
}) => {
  const { t } = useTranslation()
  const { textSize, smallTextSize } = useCheckoutSizes()
  const themeClass = useThemeClass()
  const containerClasses = clsx(styles.container, reversed && styles.reversed)
  const textClass = clsx(styles.text, styles[themeClass])
  const { cardExpiryDateColor } = useCheckoutColor()
  const selectedPaymentType = useSelectedPaymentType() ?? PaymentType.PBI
  const cashPayMonthlyEnabled = useCachedFeatureFlag(Features.CONVERGENCE_CASH_PAY_MONTHLY_ENABLED, false)
  const isCashAppPayBlocked = selectedPaymentType === PaymentType.PCL && !cashPayMonthlyEnabled
  const isAPTheme = useIsTheme(Theme.AP)
  const cashtagStyle = clsx(!isGALayout && styles.cashtag, isAPTheme && styles.italic)
  const cashappPaymentMethodTooltipId = 'cashapp-payment-method-tooltip'
  const isDisplayErrorMessage = (isCashAppPayBlocked && validatePaymentType) || status !== 'ACTIVE'

  const toggleCashAppPaymentMethodTooltip = (e: MouseEvent): void => {
    e.stopPropagation()
    setShowCashAppPaymentMethodTooltip(!showCashAppPaymentMethodTooltip)
  }

  const learnMore = (): JSX.Element => {
    return (
      <button type='button' className={styles.triggerButton} onClick={handleToggleTooltip} ref={tooltipRef}>
        <Text size={textSize} bold renderAs='span' underline>
          {t('paymentMethod:learnMore')}
        </Text>
      </button>
    )
  }

  return (
    <span className={containerClasses} {...(testNameSpace.length > 0 && { 'data-testid': testNameSpace })}>
      <span className={styles.icon}>
        <PaymentIcon.CashPay size={iconSize} title={t('paymentMethod:cashAppPay')} />
      </span>
      <span className={styles.cashAppPayContainer}>
        <span className={textClass}>
          {!isGALayout && (
            <>
              <Span size={textSize}>{t('paymentMethod:cashAppPay')}</Span>
              {!hideInfoIcon && (
                <div ref={cashAppPaymentMethodTooltipRef}>
                  <Button.Link
                    size='Small'
                    className={styles.paymentMethodTooltipIcon}
                    id={cashappPaymentMethodTooltipId}
                    onClick={toggleCashAppPaymentMethodTooltip}
                    aria-label={t('paymentMethod:moreInfo')}
                  >
                    <Icon.Info size='Small' />
                  </Button.Link>
                </div>
              )}
            </>
          )}
          <span className={cashtagStyle}>
            <Span size={textSize} color={cardExpiryDateColor}>
              {cashtag}
            </Span>
          </span>
        </span>
        {isDisplayErrorMessage && (
          <span className={textClass}>
            <Span size={smallTextSize} color='Fire'>
              {t(`paymentMethod:errors.cashAppPayNotAllowed`)}
            </Span>
          </span>
        )}
      </span>
      {showLearnMore && learnMore()}
    </span>
  )
}
